import { Tag } from '@carbon/react';
import { useDataTableContext } from '../context';
import { DataTableColumnMeta } from '../types';
import { validateColumnFilterType } from '../utils';
import { SelectFilter } from './select-filter';
import styles from './column-select-filter.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface ColumnSelectFilterProps {
  columnId: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column-bound single-select filter that syncs with DataTable's filter state.
 * Automatically validates column type compatibility and manages filter value.
 * Shows a tag indicator when multiple values are selected from elsewhere.
 * @param {ColumnSelectFilterProps} props - Component props
 * @param {string} props.columnId - ID of the column to filter
 * @param {string} props.label - Display label for the filter
 * @param {Array} props.options - Available options with value/label pairs
 * @param {string} [props.placeholder] - Placeholder text
 * @throws {Error} If column type is not 'enum' or 'boolean'
 * @return {JSX.Element} The select filter element
 */
export function ColumnSelectFilter({
  columnId,
  label,
  options,
  placeholder,
}: ColumnSelectFilterProps) {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const meta = column?.columnDef.meta as DataTableColumnMeta | undefined;

  validateColumnFilterType('select', meta?.type, columnId);

  // Support columns that use multiSelectFilter (boolean/enum types expect string[])
  const rawValue = column?.getFilterValue();
  const filterArray = Array.isArray(rawValue) ? rawValue : [];
  const hasMultiple = filterArray.length > 1;
  const value = hasMultiple ? null : (filterArray[0] ?? null);

  const handleChange = (newValue: string | null) => {
    // Wrap in array for compatibility with multiSelectFilter
    column?.setFilterValue(newValue ? [newValue] : undefined);
  };

  const handleClearAll = () => {
    column?.setFilterValue(undefined);
  };

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={`${columnId}-filter`}>
          {label}
        </label>
        {hasMultiple && (
          <Tag
            className={styles.tag}
            size="sm"
            type="gray"
            filter
            onClose={handleClearAll}
          >
            {filterArray.length} selected
          </Tag>
        )}
      </div>
      <SelectFilter
        id={`${columnId}-filter`}
        label=""
        value={value}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
      />
    </div>
  );
}
